<script setup lang="ts">
import {inject, ref} from "vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";

const props = defineProps<{
	type?: 'phone' | 'email'
}>()

const emit = defineEmits<{
	(e: 'on-call'): void,
	(e: 'on-email'): void,
	(e: 'on-copy'): void,
}>()

const $tooltip = ref<typeof AppTooltip | null>(null)
const closeDropdown = inject<() => void>('appResizableDropdownClose')

const closeOnMobile = () => {
	if (window.innerWidth <= 480) {
		closeDropdown?.()
	}
}

const onCall = () => {
	emit('on-call')
	closeOnMobile()
}

const onEmail = () => {
	emit('on-email')
	closeOnMobile()
}

const onCopy = () => {
	emit('on-copy')
	if (window.innerWidth <= 480) {
		closeOnMobile()
		return
	}
	$tooltip.value?.showByClick()
}
</script>

<template>
	<div class="email-phone-options-dropdown">
		<div
			v-if="props.type === 'phone'"
			class="email-phone-options-dropdown__item"
			@click="onCall"
		>
			<app-icon
				name="phone"
				width="14px"
				color="#979797"
			/>
			<div class="email-phone-options-dropdown__item-text">
				{{ $t('contact_call') }}
			</div>
		</div>
		<div
			v-else
			class="email-phone-options-dropdown__item"
			@click="onEmail"
		>
			<app-icon
				name="envelope"
				width="14px"
				color="#979797"
			/>
			<div class="email-phone-options-dropdown__item-text">
				{{ $t('contact_write') }}
			</div>
		</div>
		<app-tooltip
			ref="$tooltip"
			:text="$t(props.type === 'email' ? 'email_copied' : 'phone_copied')"
			position="bottom"
			show-by-activator-click
		>
			<template #activator>
				<div
					class="email-phone-options-dropdown__item"
					@click="onCopy"
				>
					<app-icon
						name="copy"
						width="14px"
						color="#979797"
					/>
					<div class="email-phone-options-dropdown__item-text">
						{{ $t('ui_copy') }}
					</div>
				</div>
			</template>
		</app-tooltip>
	</div>
</template>

<style scoped lang="scss">
.email-phone-options-dropdown {
	display: flex;
	flex-direction: column;
	height: 100%;
	width: 100%;
	background: #fff;
	padding: 2px 4px;

	&__item {
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 5px 8px;
		border-radius: 8px;
		box-sizing: border-box;

		&:hover {
			background-color: #F2F2F2;
			cursor: pointer;
		}

		&-text {
			font-size: 14px;
			line-height: 18px;
		}
	}
}

@media (max-width: 480px) {
	.email-phone-options-dropdown {
		height: auto;
		padding: 4px 12px 10px;

		&__item {
			min-height: 44px;
			padding: 8px 6px;
			border-bottom: 1px solid #F2F2F2;
			border-radius: 0;
		}

		:deep(.app-tooltip),
		:deep(.app-tooltip__activator) {
			display: block;
			width: 100%;
		}
	}
}
</style>
